import {
  InfoCircleOutlined, UserOutlined
} from '@ant-design/icons';
import { Avatar, Input, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SalesLeaderboardItem } from '../../../../types/leaderboard';
import { formatNumber, formatRupiah, glassTableContainerStyle } from '../../utils';

const { Title, Text } = Typography;

interface SalesLeaderboardTableProps {
  dataSource: SalesLeaderboardItem[];
  loading: boolean;
  page: number;
  limit: number;
  total: number;
  sortBy?: 'omzet' | 'ok' | 'rsv';
  sortOrder?: 'desc' | 'asc';
  onTableChange: (pagination: any, filters: any, sorter: any) => void;
  onSearch: (value: string) => void;
}

export const SalesLeaderboardTable: React.FC<SalesLeaderboardTableProps> = ({
  dataSource,
  loading,
  page,
  limit,
  total,
  sortBy = 'omzet',
  sortOrder = 'desc',
  onTableChange,
  onSearch,
}) => {
  const navigate = useNavigate();

  const getSortingLabel = () => {
    let colName = 'Omzet Closing';
    if (sortBy === 'ok') colName = 'Jumlah Closing (OK)';
    if (sortBy === 'rsv') colName = 'Jumlah Reservasi (RSV)';

    const dir = sortOrder === 'asc' ? 'Terendah' : 'Tertinggi';
    return `${colName} (${dir})`;
  };

  const getAntdSortOrder = (key: string) => {
    if (sortBy === key) {
      return sortOrder === 'asc' ? ('ascend' as const) : ('descend' as const);
    }
    return null;
  };

  const columns: ColumnsType<SalesLeaderboardItem> = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 75,
      align: 'center',
      fixed: 'left',
      render: (rank: number) => {
        if (!rank || rank <= 0) {
          return (
            <Text type="secondary" style={{ fontSize: '13px', fontWeight: 500 }}>
              -
            </Text>
          );
        }
        if (rank === 1) {
          return (
            <Tag color="gold" style={{ borderRadius: '4px', fontWeight: 700, padding: '1px 8px', margin: 0 }}>
              #1
            </Tag>
          );
        }
        if (rank === 2) {
          return (
            <Tag style={{ borderRadius: '4px', fontWeight: 600, color: '#475569', background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '1px 8px', margin: 0 }}>
              #2
            </Tag>
          );
        }
        if (rank === 3) {
          return (
            <Tag style={{ borderRadius: '4px', fontWeight: 600, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', padding: '1px 8px', margin: 0 }}>
              #3
            </Tag>
          );
        }
        return (
          <Text type="secondary" style={{ fontSize: '13px', fontWeight: 500 }}>
            #{rank}
          </Text>
        );
      },
    },
    {
      title: 'Nama Sales Agent',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 220,
      fixed: 'left',
      render: (name: string, record) => (
        <Space size={10} style={{ cursor: 'pointer' }}>
          <Avatar
            size={34}
            src={record.avatar}
            icon={<UserOutlined />}
            style={{
              backgroundColor: '#1890ff',
            }}
          />
          <div>
            <Text strong style={{ color: '#0f172a', display: 'block', lineHeight: 1.3 }}>
              {name}
            </Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Klik untuk lihat detail
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Kawasan',
      dataIndex: 'property_areas',
      key: 'property_areas',
      width: 170,
      render: (areas: string[] | string) => {
        const areaList = Array.isArray(areas)
          ? areas
          : typeof areas === 'string' && areas
            ? (areas as string).split(',').map((s) => s.trim()).filter(Boolean)
            : [];

        if (areaList.length === 0) {
          return <Text type="secondary" italic>-</Text>;
        }

        return (
          <Space size={[0, 4]} wrap style={{ width: '100%' }}>
            {areaList.map((area) => (
              <Tag
                key={area}
                style={{
                  borderRadius: '4px',
                  fontSize: '11px',
                  padding: '1px 6px',
                  margin: '2px 0',
                  border: '1px solid #e2e8f0',
                  background: 'rgba(255,255,255,0.7)',
                }}
              >
                {area}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: (
        <Text strong style={{ color: sortBy === 'omzet' ? '#1890ff' : 'inherit' }}>
          Omzet Closing
        </Text>
      ),
      dataIndex: 'omzet',
      key: 'omzet',
      width: 180,
      sorter: true,
      sortOrder: getAntdSortOrder('omzet'),
      align: 'right',
      render: (val: number, record) => (
        <Text strong style={{ color: '#0f172a', fontSize: '13px' }}>
          {record.omzet_rupiah || formatRupiah(val)}
        </Text>
      ),
    },
    {
      title: (
        <Text strong style={{ color: sortBy === 'ok' ? '#1890ff' : 'inherit' }}>
          Closing (OK)
        </Text>
      ),
      dataIndex: 'ok_count',
      key: 'ok',
      width: 130,
      sorter: true,
      sortOrder: getAntdSortOrder('ok'),
      align: 'center',
      render: (count: number) => (
        <Text strong style={{ color: '#0f172a' }}>
          {formatNumber(count)} Unit
        </Text>
      ),
    },
    {
      title: (
        <Text strong style={{ color: sortBy === 'rsv' ? '#1890ff' : 'inherit' }}>
          Reservasi (RSV)
        </Text>
      ),
      dataIndex: 'rsv_count',
      key: 'rsv',
      width: 140,
      sorter: true,
      sortOrder: getAntdSortOrder('rsv'),
      align: 'center',
      render: (count: number) => (
        <Text style={{ color: '#334155' }}>
          {formatNumber(count)}
        </Text>
      ),
    },
    {
      title: 'Total Leads',
      dataIndex: 'leads_count',
      key: 'leads_count',
      width: 120,
      align: 'center',
      render: (count: number) => (
        <Text style={{ color: '#334155' }}>
          {formatNumber(count || 0)}
        </Text>
      ),
    },
    {
      title: 'Konversi',
      dataIndex: 'conversion_rate',
      key: 'conversion_rate',
      width: 110,
      align: 'center',
      render: (rate: number) => {
        const val = Number(rate || 0);
        return (
          <Text strong style={{ color: '#0f172a' }}>
            {val.toFixed(1)}%
          </Text>
        );
      },
    },
  ];

  return (
    <div style={glassTableContainerStyle} className="sales-leaderboard-container">
      <style>{`
        .sales-leaderboard-container .ant-table-thead > tr > th {
          background: rgba(248, 250, 252, 0.85) !important;
          border-bottom: 1px solid rgba(203, 213, 225, 0.8) !important;
          font-weight: 700;
        }
        .sales-leaderboard-container .ant-table-tbody > tr:hover > td {
          background: rgba(241, 245, 249, 0.7) !important;
        }
        .sales-leaderboard-container .ant-table-row {
          cursor: pointer;
        }
      `}</style>

      {/* Header and Search Controls */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <Space size={6} direction="vertical">
          <Space size={6}>
            <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
              Peringkat Lengkap Sales Agent
            </Title>
            <Tooltip title="Klik baris sales mana pun untuk langsung membuka Dashboard Analisis Kinerja Individu Sales terkait.">
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '13px', cursor: 'pointer' }} />
            </Tooltip>
          </Space>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', margin: 0 }}>
            Urutan aktif: <Text strong style={{ color: '#1890ff' }}>{getSortingLabel()}</Text>
          </Text>
        </Space>

        <Space size={12} style={{ flexWrap: 'wrap' }}>
          <Input.Search
            placeholder="Cari nama sales..."
            onSearch={onSearch}
            allowClear
            style={{ width: 220 }}
          />
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Total: <Text strong>{total}</Text> Agen
          </Text>
        </Space>
      </div>

      <Table<SalesLeaderboardItem>
        dataSource={dataSource}
        columns={columns}
        rowKey="user_id"
        loading={loading}
        scroll={{ x: 1050 }}
        pagination={{
          current: page,
          pageSize: limit,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (totalCount, range) => `${range[0]}-${range[1]} dari ${totalCount} sales`,
        }}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => {
            navigate(`/performa/sales-detail/${record.user_id}`);
          },
        })}
      />
    </div>
  );
};

export default SalesLeaderboardTable;
